import { JSX } from "react";
import { Helmet } from "react-helmet-async";
import logo from "@/assets/img/logo.png";

const styles = {
  logoContainer: "text-center mb-5",
  container: "container mx-auto py-10 px-6 lg:px-16",
  title: "text-4xl font-extrabold text-center text-gray-800 leading-tight",
  description: "mt-4 text-lg text-center text-gray-600 max-w-3xl mx-auto",
  section: "p-2 md:p-4",
  sectionTitle: "text-2xl font-semibold text-gray-800",
  sectionContent: "mt-4 text-gray-700",
  list: "mt-4 list-disc pl-6 text-gray-700",
  subsectionTitle: "text-xl font-semibold text-gray-800 mt-4",
  footer: "mt-10 text-center",
  footerText: "text-sm text-gray-600",
  logo: "h-auto w-full max-w-xs mx-auto",
};

function PolicyPage(): JSX.Element {
  return (
    <main role="main" className={styles.container}>
      <Helmet>
        <title>Política de Privacidad | AppCotas</title>
        <meta
          name="description"
          content="Conoce cómo AppCotas recopila, utiliza y protege tu información personal y la de tus mascotas."
        />
        <link rel="canonical" href="https://appcotas.ai/policy" />
      </Helmet>
      <header className={styles.logoContainer}>
        <a href={"/"} aria-label="Ir a la página principal">
          <img
            width={140}
            height={50}
            src={logo}
            alt="Logo AppCotas"
            className={styles.logo}
          />
        </a>
      </header>
      <h1 className={styles.title}>Política de Privacidad</h1>
      <p className={styles.description}>
        Esta Política de Privacidad explica cómo AppCotas (&quot;AppCotas&quot;)
        recopila, utiliza y divulga información que obtenemos a través de
        nuestro servicio. Todos los demás términos no definidos en este
        documento tendrán los significados establecidos en los Términos y
        Condiciones.
      </p>
      <article>
        <section className={styles.section} aria-labelledby="info-personal">
          <h2 id="info-personal" className={styles.sectionTitle}>
            Información Personal
          </h2>
          <p className={styles.sectionContent}>
            &quot;Información Personal&quot; significa datos que, solos o en
            combinación con otros, pueden ser utilizados para identificarte,
            como: nombre, dirección, correo electrónico, número de teléfono,
            fotos de tus mascotas, datos médicos de las mismas (como historial
            de vacunación o tratamientos), y cualquier otra información que tú
            decidas proporcionar a través de la aplicación. No consideramos que
            la Información Personal incluya datos que han sido anonimizados de
            manera que no permitan identificar a una persona o mascota
            específica.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="info-recogida">
          <h2 id="info-recogida" className={styles.sectionTitle}>
            1. Información que recopilamos
          </h2>
          <p className={styles.sectionContent}>Al registrarte en AppCotas:</p>
          <ul className={styles.list}>
            <li>
              Nos proporcionas información como tu nombre, correo electrónico, y
              datos relevantes sobre tus mascotas (nombre, raza, edad, peso,
              historial médico, entre otros).
            </li>
          </ul>
          <p className={styles.sectionContent}>Uso de la aplicación:</p>
          <ul className={styles.list}>
            <li>
              Recopilamos información sobre cómo utilizas AppCotas, como
              búsquedas realizadas, interacciones con veterinarios o servicios,
              y actualizaciones en el historial de tus mascotas.
            </li>
          </ul>
          <p className={styles.sectionContent}>Datos de ubicación:</p>
          <ul className={styles.list}>
            <li>
              Podemos recopilar información sobre tu ubicación para sugerirte
              veterinarios, tiendas de mascotas, o eventos relacionados con el
              cuidado de mascotas cerca de ti. Esto se hace a través de datos
              como GPS o tu dirección IP.
            </li>
          </ul>
          <p className={styles.sectionContent}>
            Cookies y tecnologías similares:
          </p>
          <ul className={styles.list}>
            <li>
              Utilizamos cookies para mejorar tu experiencia en la aplicación.
              Estas pueden almacenar tus preferencias de uso y ayudarte a
              iniciar sesión automáticamente. Puedes deshabilitar las cookies a
              través de la configuración de tu navegador, aunque esto podría
              afectar algunas funcionalidades del servicio.
            </li>
          </ul>
        </section>
        <section className={styles.section} aria-labelledby="uso-info">
          <h2 id="uso-info" className={styles.sectionTitle}>
            2. Cómo utilizamos la información
          </h2>
          <ul className={styles.list}>
            <li>Proporcionar y personalizar nuestros servicios.</li>
            <li>
              Gestionar tu cuenta y permitir la actualización de los datos de
              tus mascotas.
            </li>
            <li>
              Mejorar las funcionalidades de AppCotas basándonos en el uso y las
              interacciones de los usuarios.
            </li>
            <li>
              Enviarte notificaciones relacionadas con recordatorios de
              vacunación, citas veterinarias, o eventos de cuidado animal.
            </li>
            <li>
              Comunicarnos contigo para soporte o actualizaciones importantes
              sobre el servicio.
            </li>
          </ul>
        </section>
        <section className={styles.section} aria-labelledby="compartir-info">
          <h2 id="compartir-info" className={styles.sectionTitle}>
            3. Compartir información
          </h2>
          <p className={styles.sectionContent}>Con terceros proveedores:</p>
          <ul className={styles.list}>
            <li>
              Podemos compartir tu información con servicios de terceros que
              colaboran con nosotros para ofrecerte funcionalidades clave, como
              procesadores de pagos o servicios de análisis.
            </li>
          </ul>
          <p className={styles.sectionContent}>En situaciones legales:</p>
          <ul className={styles.list}>
            <li>
              Divulgaremos información personal si es requerido por ley o para
              proteger nuestros derechos, propiedad, o seguridad, así como los
              de nuestros usuarios.
            </li>
          </ul>
          <p className={styles.sectionContent}>Datos agregados:</p>
          <ul className={styles.list}>
            <li>
              Podemos utilizar datos anonimizados y agregados para análisis y
              fines de mejora del servicio.
            </li>
          </ul>
        </section>
        <section className={styles.section} aria-labelledby="seguridad-info">
          <h2 id="seguridad-info" className={styles.sectionTitle}>
            4. Seguridad de tu información
          </h2>
          <p className={styles.sectionContent}>
            Tu información está protegida mediante medidas de seguridad técnicas
            y organizativas. Aunque nos esforzamos por proteger tus datos, no
            podemos garantizar una seguridad absoluta. Te recomendamos que
            protejas tu cuenta con una contraseña segura y que cierres sesión
            después de usar la aplicación.
          </p>
          <p className={styles.sectionContent}>
            Si tienes dudas sobre la seguridad de tu información en AppCotas,
            puedes contactarnos en: soporte@appcotas.com.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="datos-mascotas">
          <h2 id="datos-mascotas" className={styles.sectionTitle}>
            5. Uso de datos relacionados con mascotas
          </h2>
          <p className={styles.sectionContent}>
            En AppCotas, recopilamos datos relacionados con tus mascotas para
            ofrecerte funcionalidades específicas, como:
          </p>
          <ul className={styles.list}>
            <li>Historial médico y recordatorios personalizados.</li>
            <li>Conexión con servicios veterinarios o de cuidado animal.</li>
            <li>
              Estadísticas y reportes relacionados con la salud y bienestar de
              tus mascotas.
            </li>
          </ul>
        </section>
        <section className={styles.section} aria-labelledby="retencion-datos">
          <h2 id="retencion-datos" className={styles.sectionTitle}>
            6. Retención de datos
          </h2>
          <p className={styles.sectionContent}>
            Conservamos tu información solo mientras sea necesario para los
            propósitos establecidos en esta política, salvo que la ley exija un
            período mayor de conservación.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="menores">
          <h2 id="menores" className={styles.sectionTitle}>
            7. Menores de edad
          </h2>
          <p className={styles.sectionContent}>
            AppCotas no recopila información de menores de 18 años. Si tienes
            menos de esta edad, por favor no utilices la aplicación ni
            proporciones datos personales.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="cambios-politica">
          <h2 id="cambios-politica" className={styles.sectionTitle}>
            8. Cambios en la política
          </h2>
          <p className={styles.sectionContent}>
            Podemos actualizar esta Política de Privacidad de vez en cuando.
            Cualquier cambio será notificado a través de la aplicación o por
            correo electrónico, y entrará en vigor inmediatamente después de su
            publicación.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="derechos-usuario">
          <h2 id="derechos-usuario" className={styles.sectionTitle}>
            9. Derechos del usuario
          </h2>
          <p className={styles.sectionContent}>
            Como usuario, tienes derecho a:
          </p>
          <ul className={styles.list}>
            <li>
              Acceder a los datos personales que hemos recopilado sobre ti.
            </li>
            <li>
              Solicitar la corrección o eliminación de tus datos personales.
            </li>
            <li>
              Retirar tu consentimiento para el procesamiento de datos en
              cualquier momento.
            </li>
          </ul>
          <p className={styles.sectionContent}>
            Para ejercer estos derechos, contáctanos en: soporte@appcotas.com.
          </p>
        </section>
        <section className={styles.section} aria-labelledby="seguridad-info2">
          <h2 id="seguridad-info2" className={styles.sectionTitle}>
            10. Seguridad de la información
          </h2>
          <p className={styles.sectionContent}>
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal. Sin embargo, no podemos garantizar
            una seguridad absoluta. Te recomendamos que utilices contraseñas
            seguras y mantengas tus credenciales de acceso confidenciales.
          </p>
        </section>
      </article>
      <footer className={styles.footer} aria-label="Pie de página">
        <p className={styles.footerText}>
          Si tienes preguntas o inquietudes sobre esta política, contáctanos en:
          soporte@appcotas.com
        </p>
      </footer>
    </main>
  );
}

export default PolicyPage;
